import { CORAL_800, GRAY_400, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { PendingAttachment, PendingAttachmentStatus } from '../helpers/useChatAttachments'
import { attachmentContentType, displayFilename, isImageContentType } from '../helpers/chatAttachments'

import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { FC } from 'react'
import IconButton from '@mui/material/IconButton'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import LinearProgress from '@mui/material/LinearProgress'
import RefreshIcon from '@mui/icons-material/Refresh'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { formatBytes } from 'utils/formatting/formatBytes'
import { useTranslation } from 'react-i18next'

interface Props {
  attachment: PendingAttachment
  disabled?: boolean
  onRemove: (key: string) => void
  onRetry: (key: string) => void
}

export const PendingAttachmentChip: FC<Props> = ({ attachment, disabled = false, onRemove, onRetry }) => {
  const { t } = useTranslation(['chat'])
  const filename = displayFilename(attachment.file.name)
  const isFailed = attachment.status === PendingAttachmentStatus.FAILED
  const isUploading = attachment.status === PendingAttachmentStatus.UPLOADING
  const FileIcon = isFailed
    ? ErrorOutlineIcon
    : isImageContentType(attachmentContentType(attachment.file)) ? ImageOutlinedIcon : InsertDriveFileOutlinedIcon

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="0.6rem"
      data-testid="chat-pending-attachment"
      data-status={attachment.status}
      sx={{
        width: '17rem',
        maxWidth: '100%',
        padding: '0.6rem 0.4rem 0.6rem 0.8rem',
        border: `1px solid ${isFailed ? CORAL_800 : GRAY_400}`,
        borderRadius: '0.8rem',
        backgroundColor: WHITE,
      }}
    >

      <FileIcon sx={{ fontSize: '2rem', color: isFailed ? CORAL_800 : GRAY_700, flexShrink: 0 }} />

      <Stack flex={1} minWidth={0} gap="0.2rem">

        <Typography variant="text-sm" noWrap title={filename} sx={{ color: GRAY_900 }}>
          {filename}
        </Typography>

        <Typography variant="text-xs" noWrap sx={{ color: isFailed ? CORAL_800 : GRAY_700 }}>
          {isFailed ? t('chat:attachments.upload_failed') : formatBytes(attachment.file.size, 1)}
        </Typography>

        {isUploading &&
          <LinearProgress
            variant="determinate"
            value={attachment.progress}
            aria-label={t('chat:attachments.uploading', { filename })}
            sx={{ height: '0.3rem', borderRadius: '0.2rem' }}
          />
        }

      </Stack>

      {isFailed &&
        <IconButton
          size="small"
          aria-label={t('chat:attachments.retry', { filename })}
          disabled={disabled}
          onClick={() => onRetry(attachment.key)}
        >
          <RefreshIcon sx={{ fontSize: '1.8rem', color: GRAY_700 }} />
        </IconButton>
      }

      <IconButton
        size="small"
        aria-label={t('chat:attachments.remove', { filename })}
        disabled={disabled}
        onClick={() => onRemove(attachment.key)}
      >
        <CloseIcon sx={{ fontSize: '1.8rem', color: GRAY_700 }} />
      </IconButton>

    </Stack>
  )
}
